import React, { useEffect, useState, useCallback } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "../api/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  LineChart, Line, Legend, Cell, PieChart, Pie,
} from "recharts";
import {
  TrendingUp, AlertTriangle, Award, Download, Filter, Star,
  Users, Clock, Ticket, CheckSquare, Phone, BarChart2, ChevronDown, ChevronUp
} from "lucide-react";

const fmtMin = (m: number) => { const h = Math.floor(m/60); return `${h}:${String(m%60).padStart(2,'0')}`; };


const DEPTS = ["Technical Support","Development","Customer Support","Billing","Other"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const YEARS  = [2026,2025,2024];
const COLORS  = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#f97316","#84cc16"];

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 70 ? "bg-green-100 text-green-700" : score >= 40 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700";
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${color}`}>{score}%</span>;
}

function Stars({ score }: { score: number }) {
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(i => <Star key={i} size={12} className={i<=Math.round(score)?"text-yellow-400 fill-yellow-400":"text-gray-200 fill-gray-200"} />)}
      <span className="text-xs text-gray-500 ml-1">{score>0?score.toFixed(1):"-"}</span>
    </div>
  );
}

function RateBar({ pct, color="bg-blue-500" }: { pct: number; color?: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
        <div className={`${color} h-1.5 rounded-full`} style={{ width: `${Math.min(pct,100)}%` }} />
      </div>
      <span className="text-xs text-gray-500 w-8 text-right">{pct}%</span>
    </div>
  );
}

const Reports: React.FC = () => {
  const [performance, setPerformance] = useState<any[]>([]);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState("performance");
  const [expanded, setExpanded]       = useState<string | null>(null);

  // filters
  const now = new Date();
  const [deptFilter, setDeptFilter]   = useState("");
  const [empFilter, setEmpFilter]     = useState("");
  const [from, setFrom]               = useState("");
  const [to, setTo]                   = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [yearFilter, setYearFilter]   = useState(String(now.getFullYear()));

  const [tickets, setTickets]   = useState<any[]>([]);
  const [tasks, setTasks]       = useState<any[]>([]);
  const [projects, setProjects]  = useState<any[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const params: any = {};
    if (deptFilter)  params.department = deptFilter;
    if (empFilter)   params.userId     = empFilter;
    if (from)        params.from       = from;
    if (to)          params.to         = to;
    if (monthFilter) { params.month = String(MONTHS.indexOf(monthFilter)+1); params.year = yearFilter; }
    try {
      const [perfRes, tRes, taskRes, projRes] = await Promise.all([
        api.get("/reports/performance", { params }),
        api.get("/reports/tickets"),
        api.get("/reports/tasks"),
        api.get("/reports/projects", { params }),
      ]);
      setPerformance(perfRes.data);
      setTickets(tRes.data);
      setTasks(taskRes.data);
      setProjects(projRes.data || []);
    } finally { setLoading(false); }
  }, [deptFilter, empFilter, from, to, monthFilter, yearFilter]);

  useEffect(() => { load(); }, []);

  const reset = () => { setDeptFilter(""); setEmpFilter(""); setFrom(""); setTo(""); setMonthFilter(""); setYearFilter(String(now.getFullYear())); };

  const topPerformers  = performance.slice(0, 5);
  const lowActivity    = performance.filter(p => p.lowActivity);
  const needsImprove   = performance.filter(p => p.needsImprovement);

  // ── Export helpers ─────────────────────────────────────────────
  const getExportData = () => {
    if (activeTab === "performance") return {
      headers: ["Name","Department","Role","Tickets Done","Open Tickets","Tasks Done","In Progress","Hours","Quality Score","CRM","Comms","Performance Score","Low Activity"],
      rows: performance.map(p => [p.name, p.department||"", p.role, p.completedTickets, p.openTickets, p.completedTasks, p.inProgTasks, p.totalHours, p.avgQuality, p.crmCount, p.communicationsCount, p.performanceScore, p.lowActivity?"Yes":"No"]),
      filename: "team-performance",
    };
    if (activeTab === "tickets") return {
      headers: ["Ticket#","Title","Assigned To","Department","Status","Time Spent","Date"],
      rows: tickets.map((t:any) => [t.ticketNumber, t.title, t.assignedTo?.name||"—", t.department||"—", t.status, t.timeSpent>0?fmtMin(t.timeSpent):"—", new Date(t.createdAt).toLocaleDateString("en-GB")]),
      filename: "tickets-report",
    };
    if (activeTab === "tasks") return {
      headers: ["Title","Assigned To","Department","Status","Hours Logged","Date"],
      rows: tasks.map((t:any) => [t.title, t.assignedTo?.name||"—", t.department||"—", t.status, t.totalLoggedMinutes>0?fmtMin(t.totalLoggedMinutes):"—", new Date(t.createdAt).toLocaleDateString("en-GB")]),
      filename: "tasks-report",
    };
    if (activeTab === "monthly") return {
      headers: ["Month","Employee","Department","Hours","Tickets Done","Tasks Done"],
      rows: performance.flatMap((p:any) => (p.monthly||[]).map((m:any) => [p.name, p.department||"—", m.month, m.tickets, m.tasksCompleted, m.hours, m.avgQuality||0])),
      filename: "monthly-report",
    };
    return { headers: [], rows: [], filename: "report" };
  };

  const exportExcel = () => {
    const { headers, rows, filename } = getExportData();
    if (!rows.length) return;
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${filename}-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const exportPDF = () => {
    const { headers, rows, filename } = getExportData();
    if (!rows.length) return;
    const doc = new jsPDF({ orientation: rows[0]?.length > 6 ? "landscape" : "portrait" });
    doc.setFontSize(14);
    doc.text(`${filename.replace(/-/g," ").toUpperCase()} — ${new Date().toLocaleDateString("en-GB")}`, 14, 15);
    autoTable(doc, {
      head: [headers],
      body: rows.map(r => r.map(String)),
      startY: 22,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.save(`${filename}-${new Date().toISOString().slice(0,10)}.pdf`);
  };

  // Chart data
  const hoursChart   = performance.map(p => ({ name: p.name.split(" ")[0], hours: p.totalHours }));
  const ticketChart  = performance.map(p => ({ name: p.name.split(" ")[0], done: p.completedTickets, open: p.openTickets }));
  const taskChart    = performance.map(p => ({ name: p.name.split(" ")[0], done: p.completedTasks, pending: p.pendingTasks }));
  const qualityChart = performance.map(p => ({ name: p.name.split(" ")[0], quality: p.avgQuality }));
  const scoreChart   = performance.slice(0,8).map(p => ({ name: p.name.split(" ")[0], score: p.performanceScore }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Reports & Analytics</h2>
        <div className="flex items-center gap-2">
          <button onClick={exportExcel} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 font-medium">
            <Download size={14} /> Excel
          </button>
          <button onClick={exportPDF} className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 font-medium">
            <Download size={14} /> PDF
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { key:"performance", label:"Team Performance" },
          { key:"charts",      label:"Charts" },
          { key:"monthly",     label:"Monthly Summary" },
          { key:"tickets",     label:"Tickets Report" },
          { key:"tasks",       label:"Tasks Report" },
          { key:"projects",    label:"Project Reports" },
        ].map(t => (
          <button key={t.key} onClick={()=>setActiveTab(t.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab===t.key?"bg-white text-blue-600 shadow-sm":"text-gray-600 hover:text-gray-800"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── FILTERS ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <select value={deptFilter} onChange={e=>setDeptFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">All Departments</option>
            {DEPTS.map(d=><option key={d}>{d}</option>)}
          </select>
          <select value={empFilter} onChange={e=>setEmpFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">All Employees</option>
            {performance.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={monthFilter} onChange={e=>setMonthFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">All Months</option>
            {MONTHS.map(m=><option key={m}>{m}</option>)}
          </select>
          <select value={yearFilter} onChange={e=>setYearFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            {YEARS.map(y=><option key={y}>{y}</option>)}
          </select>
          <input type="date" value={from} onChange={e=>{setFrom(e.target.value);setMonthFilter("");}}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
          <input type="date" value={to} onChange={e=>{setTo(e.target.value);setMonthFilter("");}}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"/>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={load} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700">
            <Filter size={14}/> Apply
          </button>
          <button onClick={()=>{reset();}} className="border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Reset</button>
        </div>
      </div>

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"/></div>}

      {!loading && activeTab === "performance" && (
        <div className="space-y-5">
          {/* Alerts */}
          {lowActivity.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle size={16} className="text-orange-600"/><span className="font-semibold text-orange-700 text-sm">Low Activity Alert</span></div>
              <div className="flex flex-wrap gap-2">
                {lowActivity.map((e:any)=>(
                  <span key={e.id} className="bg-orange-100 text-orange-700 text-xs px-3 py-1 rounded-full">
                    {e.name} — {e.totalHours}h / {e.completedTasks} tasks
                  </span>
                ))}
              </div>
            </div>
          )}
          {needsImprove.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle size={16} className="text-red-600"/><span className="font-semibold text-red-700 text-sm">Low Quality Score Alert</span></div>
              <div className="flex flex-wrap gap-2">
                {needsImprove.map((e:any)=>(
                  <span key={e.id} className="bg-red-100 text-red-700 text-xs px-3 py-1 rounded-full">{e.name} ({e.avgQuality}⭐)</span>
                ))}
              </div>
            </div>
          )}

          {/* Top Performers */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3"><Award size={18} className="text-yellow-500"/><h3 className="font-semibold text-gray-800">Top Performers This Period</h3></div>
            <div className="flex flex-wrap gap-3">
              {topPerformers.map((p,i)=>(
                <div key={p.id} className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-3 min-w-[200px]">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${i===0?"bg-yellow-500":i===1?"bg-gray-400":i===2?"bg-amber-600":"bg-blue-400"}`}>#{i+1}</div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{p.name}</p>
                    <p className="text-xs text-gray-500">{p.department||"—"}</p>
                    <ScoreBadge score={p.performanceScore}/>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Performance Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <TrendingUp size={18} className="text-blue-600"/>
              <h3 className="font-semibold text-gray-800">Team Performance Overview</h3>
              <span className="ml-auto text-xs text-gray-400">{performance.length} employees</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {["#","Employee","Dept","Tickets ✓/Open","Tasks ✓/Prog","Hours","Avg h/day","Quality","CRM","Comms","Score",""].map(h=>(
                      <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {performance.map((p, i) => (
                    <React.Fragment key={p.id}>
                      <tr className={`hover:bg-gray-50 ${p.lowActivity?"bg-orange-50/30":""}`}>
                        <td className="px-3 py-2.5 text-gray-400 text-xs">{i+1}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">{p.name[0]}</div>
                            <div>
                              <p className="font-medium text-gray-800 text-xs">{p.name}</p>
                              <p className="text-xs text-gray-400 capitalize">{p.role?.toLowerCase().replace("_"," ")}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-500">{p.department||"—"}</td>
                        <td className="px-3 py-2.5">
                          <div className="text-xs"><span className="text-green-600 font-semibold">{p.completedTickets}</span><span className="text-gray-400"> / </span><span className="text-blue-500">{p.openTickets}</span></div>
                          <RateBar pct={p.ticketCompletionRate} color="bg-green-400"/>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="text-xs"><span className="text-green-600 font-semibold">{p.completedTasks}</span><span className="text-gray-400"> / </span><span className="text-yellow-500">{p.inProgTasks}</span></div>
                          <RateBar pct={p.taskCompletionRate} color="bg-blue-400"/>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`font-semibold text-sm ${p.lowActivity?"text-orange-500":"text-gray-700"}`}>{p.totalHours}h</span>
                          {p.lowActivity && <span className="block text-xs text-orange-400">⚠ Low</span>}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-500">{p.avgHoursPerDay}h</td>
                        <td className="px-3 py-2.5"><Stars score={p.avgQuality}/></td>
                        <td className="px-3 py-2.5 text-xs text-gray-600">{p.crmCount}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600">{p.communicationsCount}</td>
                        <td className="px-3 py-2.5"><ScoreBadge score={p.performanceScore}/></td>
                        <td className="px-3 py-2.5">
                          <button onClick={()=>setExpanded(expanded===p.id?null:p.id)} className="text-gray-400 hover:text-blue-600">
                            {expanded===p.id?<ChevronUp size={15}/>:<ChevronDown size={15}/>}
                          </button>
                        </td>
                      </tr>
                      {expanded===p.id && (
                        <tr className="bg-blue-50/50">
                          <td colSpan={12} className="px-4 py-3">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <p className="text-gray-400 mb-1">Workload</p>
                                <p>Assigned: <b>{p.totalTasks}</b></p>
                                <p>Completed: <b className="text-green-600">{p.completedTasks}</b></p>
                                <p>In Progress: <b className="text-yellow-600">{p.inProgTasks}</b></p>
                                <p>Pending: <b className="text-gray-500">{p.pendingTasks}</b></p>
                              </div>
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <p className="text-gray-400 mb-1">Tickets</p>
                                <p>Total: <b>{p.totalTickets}</b></p>
                                <p>Closed: <b className="text-green-600">{p.completedTickets}</b></p>
                                <p>Open: <b className="text-blue-500">{p.openTickets}</b></p>
                                <p>In Progress: <b className="text-yellow-500">{p.inProgTickets}</b></p>
                              </div>
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <p className="text-gray-400 mb-1">Customer Interactions</p>
                                <p>CRM Entries: <b>{p.crmCount}</b></p>
                                <p>Communications: <b>{p.communicationsCount}</b></p>
                                <p>Quality Reviews: <b>{p.totalQualityReviews}</b></p>
                                <p>Projects: <b>{p.projectsCount}</b></p>
                              </div>
                              <div className="bg-white rounded-lg p-3 border border-gray-100">
                                <p className="text-gray-400 mb-1">Activity Distribution</p>
                                {Object.entries(p.activityDist||{}).map(([k,v]:any)=>(
                                  <div key={k} className="flex items-center gap-1 mb-0.5">
                                    <span className="capitalize w-20">{k}:</span>
                                    <div className="flex-1 bg-gray-100 rounded h-1.5">
                                      <div className="bg-blue-400 h-1.5 rounded" style={{width:`${v}%`}}/>
                                    </div>
                                    <span>{v}%</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {performance.length===0 && <div className="text-center py-10 text-gray-400">No data found</div>}
            </div>
          </div>
        </div>
      )}

      {!loading && activeTab==="charts" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Hours by Employee */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Clock size={16} className="text-purple-500"/>Hours Logged</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={hoursChart} margin={{top:5,right:10,left:-10,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="name" tick={{fontSize:11}}/>
                <YAxis tick={{fontSize:11}}/>
                <Tooltip/>
                <Bar dataKey="hours" fill="#8b5cf6" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tickets */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Ticket size={16} className="text-orange-500"/>Tickets Overview</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={ticketChart} margin={{top:5,right:10,left:-10,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="name" tick={{fontSize:11}}/>
                <YAxis tick={{fontSize:11}}/>
                <Tooltip/>
                <Legend wrapperStyle={{fontSize:11}}/>
                <Bar dataKey="done" name="Closed" fill="#10b981" radius={[4,4,0,0]}/>
                <Bar dataKey="open" name="Open" fill="#f59e0b" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tasks */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><CheckSquare size={16} className="text-green-500"/>Tasks Completed</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={taskChart} margin={{top:5,right:10,left:-10,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="name" tick={{fontSize:11}}/>
                <YAxis tick={{fontSize:11}}/>
                <Tooltip/>
                <Legend wrapperStyle={{fontSize:11}}/>
                <Bar dataKey="done" name="Completed" fill="#3b82f6" radius={[4,4,0,0]}/>
                <Bar dataKey="pending" name="Pending" fill="#d1d5db" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Quality Score */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Star size={16} className="text-yellow-500"/>Quality Score Comparison</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={qualityChart} margin={{top:5,right:10,left:-10,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="name" tick={{fontSize:11}}/>
                <YAxis domain={[0,5]} tick={{fontSize:11}}/>
                <Tooltip/>
                <Bar dataKey="quality" name="Avg Quality" fill="#f59e0b" radius={[4,4,0,0]}>
                  {qualityChart.map((e,i)=><Cell key={i} fill={e.quality>=4?"#10b981":e.quality>=2.5?"#f59e0b":"#ef4444"}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Performance Score */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 col-span-full">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><BarChart2 size={16} className="text-blue-500"/>Overall Performance Score</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={scoreChart} layout="vertical" margin={{top:5,right:30,left:60,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false}/>
                <XAxis type="number" domain={[0,100]} tick={{fontSize:11}}/>
                <YAxis dataKey="name" type="category" tick={{fontSize:11}} width={60}/>
                <Tooltip formatter={(v:any)=>`${v}%`}/>
                <Bar dataKey="score" name="Score" radius={[0,4,4,0]}>
                  {scoreChart.map((e,i)=><Cell key={i} fill={e.score>=70?"#10b981":e.score>=40?"#f59e0b":"#ef4444"}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {!loading && activeTab==="monthly" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">Monthly Activity Summary</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Employee","Department","Month","Tickets","Tasks","Hours","Avg Quality"].map(h=>(
                    <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {performance.flatMap(p =>
                  (p.monthly||[]).map((m:any) => (
                    <tr key={`${p.id}-${m.month}`} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-800">{p.name}</td>
                      <td className="px-4 py-2 text-gray-500 text-xs">{p.department||"—"}</td>
                      <td className="px-4 py-2 text-gray-600 text-xs">{MONTHS[parseInt(m.month.split("-")[1])-1]} {m.month.split("-")[0]}</td>
                      <td className="px-4 py-2 text-green-600 font-semibold">{m.tickets}</td>
                      <td className="px-4 py-2 text-blue-600 font-semibold">{m.tasksCompleted}</td>
                      <td className="px-4 py-2 text-purple-600 font-semibold">{m.hours}h</td>
                      <td className="px-4 py-2"><Stars score={m.avgQuality}/></td>
                    </tr>
                  ))
                )}
                {performance.every(p=>!p.monthly?.length) && (
                  <tr><td colSpan={7} className="text-center py-10 text-gray-400">No monthly data available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && activeTab==="tickets" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100"><h3 className="font-semibold text-gray-800">Tickets Report ({tickets.length})</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>{["Ticket#","Title","Assigned To","Department","Status","Time Spent","Date"].map(h=>(
                  <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {tickets.slice(0,100).map((t:any)=>(
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs text-blue-600">{t.ticketNumber}</td>
                    <td className="px-4 py-2 text-gray-800 max-w-[200px] truncate">{t.title}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{t.assignedTo?.name||"—"}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{t.department||"—"}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${t.status==="CLOSED"?"bg-green-100 text-green-700":t.status==="OPEN"?"bg-blue-100 text-blue-700":"bg-yellow-100 text-yellow-700"}`}>{t.status}</span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{t.timeSpent>0?fmtMin(t.timeSpent):"—"}</td>
                    <td className="px-4 py-2 text-gray-400 text-xs">{new Date(t.createdAt).toLocaleDateString("en-GB")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && activeTab==="tasks" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100"><h3 className="font-semibold text-gray-800">Tasks Report ({tasks.length})</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>{["Title","Assigned To","Department","Status","Hours Logged","Date"].map(h=>(
                  <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {tasks.slice(0,100).map((t:any)=>(
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-800 max-w-[200px] truncate">{t.title}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{t.assignedTo?.name||"—"}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{t.department||"—"}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${t.status==="COMPLETED"?"bg-green-100 text-green-700":t.status==="IN_PROGRESS"?"bg-yellow-100 text-yellow-700":"bg-gray-100 text-gray-600"}`}>{t.status}</span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{t.totalLoggedMinutes>0?fmtMin(t.totalLoggedMinutes):"—"}</td>
                    <td className="px-4 py-2 text-gray-400 text-xs">{new Date(t.createdAt).toLocaleDateString("en-GB")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && activeTab==="projects" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Project Reports ({projects.length})</h3>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>Total Tasks: <b className="text-gray-700">{projects.reduce((s: number, p: any) => s + (p.totalTasks || 0), 0)}</b></span>
              <span>Total Hours: <b className="text-blue-600">{(() => { const m = projects.reduce((s: number, p: any) => s + (p.totalMinutes || 0), 0); return m >= 60 ? `${Math.floor(m/60)}h ${m%60 > 0 ? (m%60)+"m":""}`.trim() : `${m}m`; })()}</b></span>
            </div>
          </div>
          {projects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <svg className="w-12 h-12 mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
              <p className="text-sm font-medium">No project data available</p>
              <p className="text-xs mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <th className="px-4 py-3">Project</th>
                    <th className="px-4 py-3 text-center">Tasks</th>
                    <th className="px-4 py-3 text-center">Open</th>
                    <th className="px-4 py-3 text-center">In Progress</th>
                    <th className="px-4 py-3 text-center">On Hold</th>
                    <th className="px-4 py-3 text-center">Done</th>
                    <th className="px-4 py-3 text-center">Hours</th>
                    <th className="px-4 py-3 text-center">Est.</th>
                    <th className="px-4 py-3 text-center">Work Entries</th>
                    <th className="px-4 py-3 text-center">Employees</th>
                    <th className="px-4 py-3 text-center">Last Activity</th>
                    <th className="px-4 py-3 text-center">Progress</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {projects.map((p: any) => {
                    const totalMins = p.totalMinutes || 0;
                    const hrs = Math.floor(totalMins / 60);
                    const mins = totalMins % 60;
                    const hoursFmt = hrs > 0 ? `${hrs}h ${mins > 0 ? mins + "m" : ""}`.trim() : totalMins > 0 ? `${mins}m` : "\u2014";
                    const pct = p.totalTasks > 0 ? Math.round((p.completed / p.totalTasks) * 100) : 0;
                    const lastAct = p.lastActivity ? new Date(p.lastActivity).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "\u2014";
                    return (
                      <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-800">{p.name}</p>
                            {p.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{p.description}</p>}
                            <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${p.status === "COMPLETED" ? "bg-green-100 text-green-700" : p.status === "CANCELLED" ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-700"}`}>{p.status}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center font-semibold text-gray-700">{p.totalTasks}</td>
                        <td className="px-4 py-3 text-center">
                          {p.open > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{p.open}</span> : <span className="text-gray-300">0</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {p.inProgress > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">{p.inProgress}</span> : <span className="text-gray-300">0</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {p.onHold > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">{p.onHold}</span> : <span className="text-gray-300">0</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {p.completed > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">{p.completed}</span> : <span className="text-gray-300">0</span>}
                        </td>
                        <td className="px-4 py-3 text-center font-semibold text-blue-600">{hoursFmt}</td>
                        <td className="px-4 py-3 text-center text-xs text-gray-400">{p.estimatedHours ? `${p.estimatedHours}h` : "\u2014"}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{p.directWorkLogs || 0}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{p.employeeCount}</td>
                        <td className="px-4 py-3 text-center text-xs text-gray-500">{lastAct}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 justify-center">
                            <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${pct === 100 ? "bg-green-500" : pct > 50 ? "bg-blue-500" : pct > 0 ? "bg-yellow-500" : "bg-gray-300"}`} style={{width: `${pct}%`}}/>
                            </div>
                            <span className="text-xs font-medium text-gray-500 w-8">{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-semibold text-sm border-t-2 border-gray-200">
                    <td className="px-4 py-3 text-gray-700">Total ({projects.length} projects)</td>
                    <td className="px-4 py-3 text-center text-gray-700">{projects.reduce((s: number, p: any) => s + (p.totalTasks || 0), 0)}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{projects.reduce((s: number, p: any) => s + (p.open || 0), 0)}</td>
                    <td className="px-4 py-3 text-center text-yellow-700">{projects.reduce((s: number, p: any) => s + (p.inProgress || 0), 0)}</td>
                    <td className="px-4 py-3 text-center text-orange-700">{projects.reduce((s: number, p: any) => s + (p.onHold || 0), 0)}</td>
                    <td className="px-4 py-3 text-center text-green-700">{projects.reduce((s: number, p: any) => s + (p.completed || 0), 0)}</td>
                    <td className="px-4 py-3 text-center text-blue-700">{(() => { const m = projects.reduce((s: number, p: any) => s + (p.totalMinutes || 0), 0); return m >= 60 ? `${Math.floor(m/60)}h ${m%60 > 0 ? " "+(m%60)+"m":""}`.trim() : `${m}m`; })()}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{(() => { const h = projects.reduce((s: number, p: any) => s + (p.estimatedHours || 0), 0); return h > 0 ? `${h}h` : "\u2014"; })()}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{projects.reduce((s: number, p: any) => s + (p.directWorkLogs || 0), 0)}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{projects.reduce((s: number, p: any) => s + (p.employeeCount || 0), 0)}</td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
};
export default Reports;
